import { useState, useCallback, useRef } from 'react';
import { CallBackProps, STATUS, ACTIONS, EVENTS, Step } from 'react-joyride';
import { calendarTutorialSteps } from '@/lib/calendar-tutorial-steps';

export interface TutorialCallbacks {
  openFilter?: () => void;
  closeFilter?: () => void;
  openSettings?: () => void;
  closeSettings?: () => void;
  openActionsPanel?: () => void;
  closeActionsPanel?: () => void;
  setActionsPanelMode?: (mode: 'employees' | 'shift-templates-editing' | 'day-actions') => void;
}

/**
 * Custom hook for managing the interactive calendar tutorial state
 * Uses React Joyride for step-by-step guided tours with element highlighting
 */
export const useCalendarTutorial = (callbacks?: TutorialCallbacks) => {
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [steps] = useState<Step[]>(calendarTutorialSteps);
  const callbacksRef = useRef<TutorialCallbacks>(callbacks || {});

  /**
   * Update callbacks ref when they change
   */
  callbacksRef.current = callbacks || {};

  /**
   * Execute step-specific actions (like opening popovers)
   */
  const executeStepAction = useCallback((stepIdx: number) => {
    const step = steps[stepIdx];
    const target = typeof step.target === 'string' ? step.target : '';

    console.log(`Tutorial step ${stepIdx + 1}/${steps.length} - Executing action for:`, target);

    // Map of step targets to actions
    const stepActions: Record<string, () => void> = {
      '[data-tutorial="filter-dropdown"]': () => {
        console.log('Opening filter dropdown...');
        callbacksRef.current.openFilter?.();
      },
      '[data-tutorial="settings-button"]': () => {
        console.log('Opening settings popover...');
        callbacksRef.current.openSettings?.();
      },
      '[data-tutorial="actions-toggle"]': () => {
        console.log('Opening actions panel...');
        callbacksRef.current.openActionsPanel?.();
      },
      '[data-tutorial="actions-panel"]': () => {
        console.log('Ensuring actions panel is open...');
        callbacksRef.current.openActionsPanel?.();
      },
      '[data-tutorial="employee-list"]': () => {
        console.log('Opening actions panel (employee mode)...');
        callbacksRef.current.openActionsPanel?.();
        setTimeout(() => callbacksRef.current.setActionsPanelMode?.('employees'), 150);
      },
      '[data-tutorial="shift-templates"]': () => {
        console.log('Opening actions panel (shift templates mode)...');
        callbacksRef.current.openActionsPanel?.();
        setTimeout(() => callbacksRef.current.setActionsPanelMode?.('shift-templates-editing'), 150);
      },
      '[data-tutorial="day-templates"]': () => {
        console.log('Opening actions panel (day actions mode)...');
        callbacksRef.current.openActionsPanel?.();
        setTimeout(() => callbacksRef.current.setActionsPanelMode?.('day-actions'), 150);
      },
    };

    // Execute action if defined for this step
    if (stepActions[target]) {
      setTimeout(() => stepActions[target](), 150);
    }
  }, [steps]);

  /**
   * Handle Joyride callback events
   * Manages step progression, skip, finish, and error handling
   */
  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { status, action, index, type, lifecycle } = data;

    // Handle finished or skipped tours
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      // Close any open popovers
      callbacksRef.current.closeFilter?.();
      callbacksRef.current.closeSettings?.();
      setRun(false);
      setStepIndex(0);
      return;
    }

    // Execute step action when tooltip shows
    if (type === EVENTS.TOOLTIP && lifecycle === 'init') {
      executeStepAction(index);
    }

    // Handle step changes
    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);

      if (action === ACTIONS.NEXT) {
        setStepIndex(nextStepIndex);
      } else if (action === ACTIONS.PREV) {
        setStepIndex(nextStepIndex);
      }
    }

    // Handle close button
    if (action === ACTIONS.CLOSE) {
      callbacksRef.current.closeFilter?.();
      callbacksRef.current.closeSettings?.();
      setRun(false);
      setStepIndex(0);
    }

    // Handle target not found - wait a bit then try again or skip
    if (type === EVENTS.TARGET_NOT_FOUND) {
      const currentStep = steps[index];
      const target = typeof currentStep.target === 'string' ? currentStep.target : '';
      console.warn(`Tutorial step ${index + 1}/${steps.length} - Target not found:`, target);

      // Check if element exists in DOM but might need more time
      const element = document.querySelector(target);

      if (element) {
        console.log('Element exists but Joyride cannot find it yet, retrying...');
        // Element exists, just needs time for Joyride to detect it
        setTimeout(() => {
          setStepIndex(index); // Retry same step
        }, 500);
      } else {
        console.log('Element does not exist in DOM, skipping to next step...');
        // Element doesn't exist (e.g., no shifts created yet), skip to next
        setTimeout(() => {
          setStepIndex(index + 1);
        }, 200);
      }
    }
  }, [steps, executeStepAction]);

  /**
   * Start the tutorial from the beginning
   */
  const startTutorial = useCallback(() => {
    setStepIndex(0);
    setRun(true);
  }, []);

  /**
   * Reset tutorial to initial state
   */
  const resetTutorial = useCallback(() => {
    setStepIndex(0);
    setRun(false);
  }, []);

  /**
   * Stop the tutorial
   */
  const stopTutorial = useCallback(() => {
    setRun(false);
  }, []);

  /**
   * Resume the tutorial from current step
   */
  const resumeTutorial = useCallback(() => {
    setRun(true);
  }, []);

  return {
    run,
    stepIndex,
    steps,
    handleJoyrideCallback,
    startTutorial,
    resetTutorial,
    stopTutorial,
    resumeTutorial,
  };
};
